import { Component, Input, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { ProductCard } from '../../../product/models/product-card';
import { ProductTypes } from '../../../product/models/models';
import { ITableData } from '../../models/table';
import { ProductsService } from '../../services/products.service';
import { Observable, of, ReplaySubject, Subject, Subscription, switchMap } from 'rxjs';
import { RequestParams } from '../../models/requestParams';
import { catchError, finalize } from 'rxjs/operators';
import { SnackBarService } from '../../services/snack-bar.service';
import { PhSimpleTableComponent } from '../ph-simple-table/ph-simple-table.component';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { DwUserActivityNames } from '../../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../models/dwUsersActivityTypes';
import { TranslateService } from '@ngx-translate/core';
import { MixpanelService } from '../../services/mixpanel.service';
import { ExcelService } from '../../services/xls.service';
import { DwUsersActivityService } from '../../services/dw-users-activity.service';

import { SHARED_MODULES } from '../../../shared.';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { SearchComponent } from '../search/search.component';
import { DownloadService } from '../../services/download.service';

@Component({
    selector: 'app-product-usage-table[product]',
    templateUrl: './product-usage-table.component.html',
    styleUrls: ['./product-usage-table.component.scss'],
	imports: [
		SHARED_MODULES,
		PhIconWrapperComponent,
		PhLoaderComponent,
		PhSimpleTableComponent,
		SearchComponent
	],
    standalone: true
})
export class ProductUsageTableComponent implements OnInit, OnDestroy {

	private _loadData$: ReplaySubject<void> = new ReplaySubject<void>(1);
	private _requestParams: RequestParams;
	private _subscriptions: Array<Subscription> = [];

	public isExporting: WritableSignal<boolean> = signal<boolean>(false);
	public productTypes: typeof ProductTypes = ProductTypes;
	public showLoader: boolean = false;
	public sorting$: Subject<string> = new Subject<string>();
	public tableData: ITableData<any>;
	public update$: Subject<void> = new Subject<void>();

	@Input() product: ProductCard;

	@ViewChild(PhSimpleTableComponent) private _tableComponent: PhSimpleTableComponent;

	constructor(
		private translate: TranslateService,
		private productsService: ProductsService,
		private snackBarService: SnackBarService,
		private mixPanelService: MixpanelService,
		private excelService: ExcelService,
		private dwUsersActivityService: DwUsersActivityService,
        private downloadService: DownloadService
	) {}

	ngOnInit(): void {
		this._requestParams = new RequestParams({
			page: 1,
			page_size: 30
		});

		this._subscriptions.push(
			this._loadData$
				.pipe(
					switchMap(() => {
						return this.productsService
							.getProductUsage(this.product.id, this._requestParams.getParams())
							.pipe(
								finalize(() => this.showLoader = false),
								catchError(() => {
									this.snackBarService.setErrorNotification();
									return of({
										has_data: false,
										next: null,
										count: 0,
										results: {
											columns: [],
											dataSource: []
										}
									});
								})
							);
					})
				)
				.subscribe((response: ITableData<any>) => {
					this.product.initLayout(response);
					this.tableData = this.product.layout.data;
				}),

			this.sorting$.subscribe((value: string) => {
				this._requestParams.updateParams({ ordering: value !== 'default' ? value : '' });
				this.loadData();
			}),

			this.update$
				.pipe(
					switchMap(() => {
						this._requestParams.updateParams({
							page: this._requestParams.getParam('page') + 1
						});
						return this.productsService.getProductUsage(this.product.id, this._requestParams.getParams());
					})
				)
				.subscribe((response: ITableData<any>) => {
					this.product.updateLayout(response);
					this.tableData = this.product.layout.data;
				})
		);

		if (this.product.loadedLayout) {
			this.tableData = this.product.layout.data;
		} else {
			this.showLoader = true;
			this.loadData();
		}
	}

	public export(): void {
		const tabLabel: string = this.translate.instant('COMMON.LAYOUT');
		const fileName: string = `${tabLabel} ${this.product.name}`;
        this.isExporting.set(true);

        const request$: Observable<void> = this.excelService
            .getProductsLayout(this._requestParams.getParams(['ordering', 'search']), fileName, this.product.id)
            .pipe(finalize(() => this.isExporting.set(false)));

        this.downloadService.excelFilesDownloadSubject.next({
            name: fileName,
            getData$: request$
        });

		this.mixPanelService.trackPage(MixpanelEvents.ProductLayoutExcel);
		this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.DownloadProductLayout);
	}

	public loadChildren$(key: string): Observable<any> {
		return this.productsService.getProductUsageChildren(
			this.product.id,
			key,
			this._requestParams.getParams(['ordering'])
		);
	}

	public  loadData(): void {
		this._requestParams.updateParams({ page: 1 });
		if (this._tableComponent) {
			this._tableComponent.startLoading();
			this._tableComponent.scrollToTop();
		}
		this._loadData$.next();
	}

	public onSearch(value: string): void {
		this._requestParams.updateParams({ search: value });
		this.loadData();
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}
